import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent
} from '@mui/material';
import {
  TrendingUp,
  AccountBalance,
  Assessment
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { creditAPI, verificationAPI } from '../../services/api';
import { SessionManager } from '../../utils/sessionManager';
import Layout from '../../components/Layout';
import APIUsageChart from '../../components/APIUsageChart';

const Dashboard = () => {
  const [stats, setStats] = useState({
    today: 0,
    thisMonth: 0,
    totalAmount: 0,
    used: 0
  });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = SessionManager.getUser();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [creditsRes, statsRes, historyRes] = await Promise.all([
        creditAPI.getBalance(),
        creditAPI.getStats(),
        verificationAPI.getHistory(30, 0)
      ]);

      const totalAmount = creditsRes.data.total || 0;
      const used = creditsRes.data.used || 0;

      setStats({
        today: 0,
        thisMonth: statsRes.data.spendingByType?.reduce((sum, item) => sum + item.count, 0) || 0,
        totalAmount,
        used,
        apiUsage: statsRes.data.apiUsage || []
      });

      // Process history data for chart
      const apiUsageByDate = {};
      historyRes.data.forEach(log => {
        const date = new Date(log.createdat).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!apiUsageByDate[date]) {
          apiUsageByDate[date] = { date, calls: 0, cost: 0 };
        }
        apiUsageByDate[date].calls += 1;
        apiUsageByDate[date].cost += parseFloat(log.cost || 0);
      });

      const chartDataArray = Object.values(apiUsageByDate).slice(-7).reverse();
      setChartData(chartDataArray);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 0, sm: 2, md: 3 }
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 0 } }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            Dashboard Overview
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 2, sm: 3, md: 4 },
              fontSize: { xs: '0.875rem', sm: '0.875rem' }
            }}
          >
            Welcome back! Here's what's happening with your KYC platform.
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid 
          container 
          spacing={{ xs: 1.5, sm: 2, md: 3 }} 
          sx={{ 
            mb: { xs: 2, sm: 3, md: 4 },
            px: { xs: 2, sm: 0 }
          }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography 
                      color="text.secondary" 
                      variant="body2"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Today
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        my: 1,
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
                      }}
                    >
                      {stats.today}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="success.main"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      ▲ API Calls
                    </Typography>
                  </Box>
                  <TrendingUp 
                    color="success" 
                    sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography 
                      color="text.secondary" 
                      variant="body2"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      This Month
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        my: 1,
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
                      }}
                    >
                      {stats.thisMonth}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="success.main"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      ▲ API Calls
                    </Typography>
                  </Box>
                  <Assessment 
                    color="primary" 
                    sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Only show Total Amount for non-subusers */}
          {user?.accountType !== 'subuser' && (
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography 
                        color="text.secondary" 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Total Amount
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          my: 1,
                          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
                        }}
                      >
                        ₹{stats.totalAmount.toLocaleString()}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="success.main"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        ▲ Recharge
                      </Typography>
                    </Box>
                    <AccountBalance 
                      color="success" 
                      sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography 
                      color="text.secondary" 
                      variant="body2"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Used
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        my: 1,
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
                      }}
                    >
                      ₹{stats.used.toLocaleString()}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="warning.main"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      ● Recharge
                    </Typography>
                  </Box>
                  <AccountBalance 
                    color="warning" 
                    sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* API Usage by Service Chart */}
        {stats.apiUsage && stats.apiUsage.length > 0 && (
          <Box sx={{ px: { xs: 0, sm: 0 } }}>
            <APIUsageChart data={stats.apiUsage} title="API Usages by Service" />
          </Box>
        )}

        {/* Quick Actions */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2, 
            mt: { xs: 3, sm: 4 },
            px: { xs: 2, sm: 0 }
          }}
        >
          <Button
            component={RouterLink}
            to="/kyc-verification"
            variant="contained"
            size="large"
            fullWidth={{ xs: true, sm: false }}
            sx={{
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '0.9375rem', sm: '0.9375rem' }
            }}
          >
            Start Verification
          </Button>
          <Button
            component={RouterLink}
            to="/reports"
            variant="outlined"
            size="large"
            fullWidth={{ xs: true, sm: false }}
            sx={{
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '0.9375rem', sm: '0.9375rem' }
            }}
          >
            View Reports
          </Button>
        </Box>
      </Container>
    </Layout>
  );
};

export default Dashboard;
